// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type {
  DisplayTotal,
  GameResultState,
  HoleEntry,
  HoleEventEntry,
  HoleMeta,
  Player,
  ScoreTotal,
  TeamSummary,
} from '@/features/hole/types';
import { timeSync } from '@/services/appMetrics';

const normalizePlayerKey = (key: string | undefined | null) => key?.trim().toLowerCase() ?? '';
const isUuidLike = (value?: string) =>
  !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const hazardTypeFromName = (name?: string) => {
  const normalized = (name ?? '').toLowerCase();
  if (normalized.trim() === 'out') return 'out';
  if (normalized.includes('water')) return 'water';
  if (normalized.includes('penalty')) return 'penalty';
  if (normalized.includes('bunker')) return 'bunker';
  if (normalized.includes('3 putts') || normalized.includes('three putts')) return 'threeputt';
  return null;
};

export type GameResultRow = {
  key: string;
  name: string;
  isHazardGame: boolean;
  // null means no live game result yet (rendered as a dash) — merit points are never shown here.
  scores: Array<number | null>;
  // Whether the matching `scores` entry is a match standing rather than accumulated points, so the
  // table can label it "2UP". Per cell rather than per row: a player the server sent no standing
  // for falls back to points while their opponents show a margin. Absent means nothing on the row
  // is a standing.
  standings?: boolean[];
  leaders: boolean[];
  losers: boolean[];
  sharedLeaders: boolean[];
};

export const buildOrderedTeams = (teams: TeamSummary[]): TeamSummary[] => {
  if (!teams.length) return [];
  return [...teams].sort((a, b) => {
    const aKey = (a.name ?? a.id).toLowerCase();
    const bKey = (b.name ?? b.id).toLowerCase();
    return aKey.localeCompare(bKey);
  });
};

export const buildGameResultPlayers = (params: {
  players: Player[];
  orderedTeams: TeamSummary[];
  gameResults: GameResultState;
}): Player[] => {
  const { players, orderedTeams, gameResults } = params;

  if (players.length > 0) {
    if (!orderedTeams.length) return players;
    const playerById = new Map(players.map((p) => [p.id, p]));
    const ordered: Player[] = [];
    orderedTeams.forEach((team) => {
      (team.players ?? []).forEach((pid) => {
        const player = playerById.get(pid);
        if (player) ordered.push(player);
      });
    });
    const remaining = players.filter((p) => !ordered.some((op) => op.id === p.id));
    return [...ordered, ...remaining];
  }

  const keys = new Set<string>();
  Object.values(gameResults).forEach((game) => {
    Object.keys(game.scorePoints).forEach((pid) => keys.add(pid));
  });
  return Array.from(keys).map((k) => ({ id: k, name: k, createdAt: '' }));
};

const buildDisplayTotalsImpl = (params: {
  leaderboardTotals: ScoreTotal[];
  totals: ScoreTotal[];
  gameResults: GameResultState;
  leaderboardGamePoints: Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>>;
  players: Player[];
  tourTotals: Record<string, number>;
  unknownPlayerLabel: string;
}): DisplayTotal[] => {
  const { leaderboardTotals, totals, gameResults, leaderboardGamePoints, players, tourTotals, unknownPlayerLabel } = params;

  const sourceTotals = leaderboardTotals.length > 0 ? leaderboardTotals : totals;
  const normalizedSourceTotals = [...sourceTotals];
  const seenPlayerKeys = new Set<string>();
  normalizedSourceTotals.forEach((total) => {
    seenPlayerKeys.add(normalizePlayerKey(total.playerId ?? null));
    seenPlayerKeys.add(normalizePlayerKey(total.playerName ?? null));
  });
  players.forEach((player) => {
    const idKey = normalizePlayerKey(player.id);
    const nameKey = normalizePlayerKey(player.name);
    const alreadyPresent = seenPlayerKeys.has(idKey) || seenPlayerKeys.has(nameKey);
    if (alreadyPresent) {
      return;
    }
    normalizedSourceTotals.push({
      playerId: player.id,
      playerName: player.name,
      pickups: 0,
      totalStrokes: 0,
      totalRoundGamePoints: 0,
      tourGamesPoints: tourTotals[player.id] ?? 0,
      roundGamePoints: leaderboardGamePoints[player.id] ?? [],
    });
  });
  const useGameTotals = Object.keys(gameResults).length > 0;
  const playerTotals = new Map<string, number>();
  const leaderboardPointTotals = new Map<string, number>();
  // Authoritative merit totals from the round leaderboard (placement points per rules).
  const meritTotals = new Map<string, number>();

  normalizedSourceTotals.forEach((total) => {
    const match = players.find((p) => p.id === total.playerId || p.name === total.playerName);
    const key = total.playerId ?? match?.id ?? total.playerName ?? '';
    const scoreName = match?.name ?? total.playerName ?? total.playerId ?? '';

    if (leaderboardTotals.length > 0 && typeof total.totalRoundGamePoints === 'number') {
      if (key) meritTotals.set(key, total.totalRoundGamePoints);
      if (scoreName) meritTotals.set(scoreName, total.totalRoundGamePoints);
    }

    if (useGameTotals) {
      let sum = 0;
      Object.values(gameResults).forEach((game) => {
        const idKey = normalizePlayerKey(key);
        const nameKey = normalizePlayerKey(scoreName);
        // scorePoints stores the same value under several key variants for one player
        // (id, name, normalized forms) — count at most one match per game.
        let gameValue: number | null = null;
        Object.entries(game.scorePoints).forEach(([candidateKey, value]) => {
          const norm = normalizePlayerKey(candidateKey);
          if (gameValue === null && (norm === idKey || norm === nameKey)) {
            gameValue = value;
          }
        });
        sum += gameValue ?? 0;
      });

      if (key) playerTotals.set(key, sum);
      if (scoreName) playerTotals.set(scoreName, sum);
    }
  });

  Object.entries(leaderboardGamePoints).forEach(([playerKey, points]) => {
    const totalPoints = points.reduce((sum, gp) => sum + (gp.roundGamePoints ?? 0), 0);
    leaderboardPointTotals.set(playerKey, totalPoints);
  });

  const resolveRoundPoints = (key: string, name: string) =>
    meritTotals.get(key) ??
    meritTotals.get(name) ??
    leaderboardPointTotals.get(key) ??
    leaderboardPointTotals.get(name) ??
    playerTotals.get(key) ??
    playerTotals.get(name) ??
    0;

  const sorted = [...normalizedSourceTotals].sort((a, b) => {
    const aKey = a.playerId ?? a.playerName ?? '';
    const bKey = b.playerId ?? b.playerName ?? '';
    const aScoreName = a.playerName ?? a.playerId ?? '';
    const bScoreName = b.playerName ?? b.playerId ?? '';
    const aScore = resolveRoundPoints(aKey, aScoreName);
    const bScore = resolveRoundPoints(bKey, bScoreName);
    return bScore - aScore;
  });

  const leaderScores = new Map<string, number>();
  sorted.forEach((total) => {
    const key = total.playerId ?? total.playerName ?? '';
    const scoreName = total.playerName ?? total.playerId ?? '';
    leaderScores.set(key, resolveRoundPoints(key, scoreName));
  });

  const scoreValues = Array.from(leaderScores.values());
  const leaderScore = scoreValues.length ? Math.max(...scoreValues) : 0;
  const hasLeaderData = scoreValues.some((score) => score !== 0);
  const leaderKeys = new Set(
    Array.from(leaderScores.entries())
      .filter(([, score]) => score === leaderScore)
      .map(([key]) => key)
  );
  const distinctScoreCount = new Set(leaderScores.values()).size;
  const hasDistinctLeader = distinctScoreCount > 1 && leaderKeys.size > 0;

  const roundRankByIdentity = new Map<string, number>();
  if (hasLeaderData && hasDistinctLeader) {
    let previousScore: number | null = null;
    let currentRank = 0;

    sorted.forEach((item, index) => {
      const itemKey = item.playerId ?? item.playerName ?? '';
      const itemScoreName = item.playerName ?? item.playerId ?? '';
      const itemScore = resolveRoundPoints(itemKey, itemScoreName);
      if (previousScore === null || itemScore !== previousScore) {
        currentRank = index + 1;
        previousScore = itemScore;
      }
      roundRankByIdentity.set(itemKey, currentRank);
      roundRankByIdentity.set(itemScoreName, currentRank);
    });
  }

  return sorted.map((total) => {
    const match = players.find((p) => p.id === total.playerId || p.name === total.playerName);
    const key = total.playerId ?? match?.id ?? total.playerName;
    const scoreName = match?.name ?? total.playerName ?? total.playerId ?? '';
    const candidateName = match?.name ?? total.playerName ?? '';
    const displayName =
      candidateName && !isUuidLike(candidateName)
        ? candidateName
        : scoreName && !isUuidLike(scoreName)
          ? scoreName
          : unknownPlayerLabel;
    const roundPoints = useGameTotals ? resolveRoundPoints(key ?? '', scoreName) : undefined;
    const tourPoints = tourTotals[key ?? ''] ?? tourTotals[scoreName];
    const isLeader = hasLeaderData && hasDistinctLeader && (leaderKeys.has(key ?? '') || leaderKeys.has(scoreName ?? ''));
    const rank = roundRankByIdentity.get(key ?? '') ?? roundRankByIdentity.get(scoreName);

    return {
      key: key ?? scoreName,
      name: displayName,
      totalStrokes: tourPoints ?? 0,
      roundPoints,
      pickups: total.pickups ?? 0,
      isLeader,
      roundRank: rank,
    };
  });
};

export const buildTourLeaderKeys = (displayTotals: DisplayTotal[]): Set<string> => {
  let maxPoints = -Infinity;
  displayTotals.forEach((total) => {
    if (typeof total.totalStrokes !== 'number') return;
    if (total.totalStrokes > maxPoints) maxPoints = total.totalStrokes;
  });

  if (!Number.isFinite(maxPoints) || maxPoints <= 0) return new Set<string>();

  const keys = new Set<string>();
  displayTotals.forEach((total) => {
    if (total.totalStrokes === maxPoints) {
      keys.add(normalizePlayerKey(total.key));
      keys.add(normalizePlayerKey(total.name));
    }
  });
  return keys;
};

export const buildTourPlacementByKey = (displayTotals: DisplayTotal[]): Record<string, number> => {
  const map: Record<string, number> = {};
  const ranked = [...displayTotals]
    .filter((total) => typeof total.totalStrokes === 'number')
    .sort((a, b) => b.totalStrokes - a.totalStrokes);

  let previousPoints: number | null = null;
  let currentRank = 0;
  ranked.forEach((total, index) => {
    if (previousPoints === null || total.totalStrokes !== previousPoints) {
      currentRank = index + 1;
      previousPoints = total.totalStrokes;
    }
    if (currentRank > 3) return;
    const keyNorm = normalizePlayerKey(total.key);
    const nameNorm = normalizePlayerKey(total.name);
    map[keyNorm] = currentRank;
    map[nameNorm] = currentRank;
  });

  return map;
};

export const buildRoundPlacementByKey = (displayTotals: DisplayTotal[]): Record<string, number> => {
  const map: Record<string, number> = {};
  displayTotals.forEach((total) => {
    if (!total.roundRank || total.roundRank > 3) return;
    const keyNorm = normalizePlayerKey(total.key);
    const nameNorm = normalizePlayerKey(total.name);
    map[keyNorm] = total.roundRank;
    map[nameNorm] = total.roundRank;
  });
  return map;
};

/**
 * Player → team id, by id and by name. Built once per team list because a round can carry two
 * lists at once: the pairs inside each ball, and the round-level teams a Best of Team game plays.
 */
const buildTeamIdByPlayerKey = (teams: TeamSummary[], players: Player[]): Map<string, string> => {
  const teamIdByPlayerKey = new Map<string, string>();
  teams.forEach((team) => {
    (team.players ?? []).forEach((pid) => {
      teamIdByPlayerKey.set(normalizePlayerKey(pid), team.id);
    });
  });

  players.forEach((player) => {
    const teamId = teamIdByPlayerKey.get(normalizePlayerKey(player.id));
    if (teamId) {
      teamIdByPlayerKey.set(normalizePlayerKey(player.name), teamId);
    }
  });
  return teamIdByPlayerKey;
};

const buildGameResultRowsImpl = (params: {
  orderedTeams: TeamSummary[];
  /** Round-level teams, used for the games in `roundTeamGameIds` instead of `orderedTeams`. */
  roundTeams?: TeamSummary[];
  roundTeamGameIds?: ReadonlySet<string>;
  players: Player[];
  selectedGameDefinitionIds: string[];
  gameResults: GameResultState;
  leaderboardGamePoints: Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>>;
  gameDefinitionNameById: Record<string, string>;
  gameDefinitionTypes: Record<string, string>;
  gameResultPlayers: Player[];
  gameFallbackName: string;
}): GameResultRow[] => {
  const {
    orderedTeams,
    roundTeams = [],
    roundTeamGameIds,
    players,
    selectedGameDefinitionIds,
    gameResults,
    leaderboardGamePoints,
    gameDefinitionNameById,
    gameDefinitionTypes,
    gameResultPlayers,
    gameFallbackName,
  } = params;

  const ballTeamIdByPlayerKey = buildTeamIdByPlayerKey(orderedTeams, players);
  const roundTeamIdByPlayerKey = buildTeamIdByPlayerKey(roundTeams, players);

  const gameIds = new Set<string>();
  selectedGameDefinitionIds.forEach((id) => {
    if (!id) return;
    gameIds.add(id);
  });
  Object.keys(gameResults).forEach((id) => {
    if (!id) return;
    gameIds.add(id);
  });
  Object.values(leaderboardGamePoints).forEach((points) => {
    points.forEach((gp) => {
      if (!gp.gameId) return;
      gameIds.add(gp.gameId);
    });
  });

  const resolveTeamIdIn = (teamIdByPlayerKey: Map<string, string>, player: Player | undefined) =>
    teamIdByPlayerKey.get(normalizePlayerKey(player?.id)) ??
    teamIdByPlayerKey.get(normalizePlayerKey(player?.name ?? ''));

  // Which ball each player was scored in, taken from the result the server published rather than
  // re-derived here — a game played inside the ball has standings per ball, and comparing one
  // ball's leader with the other's is a matchup that does not exist. A game without balls (every
  // whole-round game, and every round with a single ball) puts everyone in one bucket, which is
  // how the round-wide standings below have always worked.
  const WHOLE_ROUND_BALL = '';
  const resolveBallKeys = (balls: GameResultState[string]['balls']): Map<string, string> => {
    const byPlayerKey = new Map<string, string>();
    (balls ?? []).forEach((ball, index) => {
      const ballKey = `ball:${index}`;
      ball.playerIds.forEach((playerId) => {
        const key = normalizePlayerKey(playerId);
        if (!key) return;
        byPlayerKey.set(key, ballKey);
        const player = players.find((candidate) => normalizePlayerKey(candidate.id) === key);
        if (player) {
          byPlayerKey.set(normalizePlayerKey(player.name), ballKey);
        }
      });
    });
    return byPlayerKey;
  };

  const rows = Array.from(gameIds).map((gameId) => {
    const game = gameResults[gameId];
    const gameName = game?.name ?? gameDefinitionNameById[gameId] ?? gameFallbackName;
    const isHazardGame = hazardTypeFromName(gameName) !== null;
    // Only live game results are shown; there is deliberately no fallback to the round
    // leaderboard's merit points — they look like game points but measure something else.
    // A game without live results renders as dashes.
    const pointScores = gameResultPlayers.map((player): number | null => {
      if (!game?.scorePoints) return null;
      const idKey = normalizePlayerKey(player.id);
      const nameKey = normalizePlayerKey(player.name);
      let score = 0;
      Object.entries(game.scorePoints).forEach(([key, value]) => {
        const norm = normalizePlayerKey(key);
        if (norm === idKey || norm === nameKey) {
          score = value;
        }
      });
      return score;
    });

    const ballKeyByPlayerKey = resolveBallKeys(game?.balls);
    const resolveBallKey = (player: Player | undefined) =>
      ballKeyByPlayerKey.get(normalizePlayerKey(player?.id)) ??
      ballKeyByPlayerKey.get(normalizePlayerKey(player?.name ?? '')) ??
      WHOLE_ROUND_BALL;

    // Match-play games read as holes up rather than accumulated points. The margin itself is
    // computed by games-service (MatchStandingCalculator) and arrives per player, already scoped to
    // the ball that player was scored in — this only looks it up. A missing standing means the game
    // has no match to show and the row keeps its accumulated points; 0 means all square.
    const leadScores = gameResultPlayers.map((player): number | undefined => {
      if (!game?.matchLead) return undefined;
      const idKey = normalizePlayerKey(player.id);
      const nameKey = normalizePlayerKey(player.name);
      let lead: number | undefined;
      Object.entries(game.matchLead).forEach(([key, value]) => {
        const norm = normalizePlayerKey(key);
        if (norm === idKey || norm === nameKey) {
          lead = value;
        }
      });
      return lead;
    });

    const scores = leadScores.map((lead, idx) => lead ?? pointScores[idx]);
    const standings = leadScores.map((lead) => lead !== undefined);

    // Best and worst are per ball as well: a game played inside the ball has a leader in each,
    // and one medal across both balls hides the other ball's winner entirely.
    const numericScoresByBall = new Map<string, number[]>();
    scores.forEach((score, idx) => {
      if (score === null) return;
      const ballKey = resolveBallKey(gameResultPlayers[idx]);
      numericScoresByBall.set(ballKey, [...(numericScoresByBall.get(ballKey) ?? []), score]);
    });
    const maxScoreByBall = new Map<string, number>();
    const minScoreByBall = new Map<string, number>();
    numericScoresByBall.forEach((ballScores, ballKey) => {
      maxScoreByBall.set(ballKey, Math.max(...ballScores));
      minScoreByBall.set(ballKey, Math.min(...ballScores));
    });

    const leaderFlags = scores.map((score, idx) => {
      if (score === null) return false;
      const maxScore = maxScoreByBall.get(resolveBallKey(gameResultPlayers[idx])) ?? 0;
      return score === maxScore && maxScore > 0;
    });
    const loserFlags = isHazardGame
      ? scores.map((score, idx) => {
          if (score === null) return false;
          const minScore = minScoreByBall.get(resolveBallKey(gameResultPlayers[idx])) ?? 0;
          return score === minScore && minScore < 0;
        })
      : scores.map(() => false);
    const isTeamGame = gameDefinitionTypes[gameId] === 'team';
    const usesRoundTeams = roundTeamGameIds?.has(gameId) ?? false;
    const teamsForGame = usesRoundTeams ? roundTeams : orderedTeams;
    const teamIdByPlayerKey = usesRoundTeams ? roundTeamIdByPlayerKey : ballTeamIdByPlayerKey;

    // A lead is shared when it is tied *inside a ball*. Two balls each having their own leader is
    // not a tie, so the buckets are kept apart here too.
    const sharedLeaders = leaderFlags.map(() => false);
    const leaderIndexesByBall = new Map<string, number[]>();
    leaderFlags.forEach((isLeader, idx) => {
      if (!isLeader) return;
      const ballKey = resolveBallKey(gameResultPlayers[idx]);
      leaderIndexesByBall.set(ballKey, [...(leaderIndexesByBall.get(ballKey) ?? []), idx]);
    });
    leaderIndexesByBall.forEach((indexes) => {
      const shared =
        isTeamGame && teamsForGame.length > 0
          ? new Set(indexes.map((idx) => resolveTeamIdIn(teamIdByPlayerKey, gameResultPlayers[idx])).filter(Boolean)).size > 1
          : indexes.length > 1;
      if (shared) {
        indexes.forEach((idx) => {
          sharedLeaders[idx] = true;
        });
      }
    });

    return {
      key: gameId,
      name: gameName,
      isHazardGame,
      scores,
      standings,
      leaders: leaderFlags,
      losers: loserFlags,
      sharedLeaders,
    };
  });

  return rows.filter((row) => {
    const hasKnownName = row.name.trim().toLowerCase() !== gameFallbackName.trim().toLowerCase();
    const hasNonZeroScore = row.scores.some((score) => typeof score === 'number' && score !== 0);
    return hasKnownName || hasNonZeroScore;
  });
};

export type MatchStatus = {
  gameId: string;
  /** Distinct per ball for a ball-scoped game, so two balls' banners don't collide. */
  bannerKey?: string;
  /** The ball this match was played in, or null for a whole-round game. */
  ballNumber?: number | null;
  gameName: string;
  state: 'dormie' | 'won';
  teamId: string;
  teamName: string | null;
  teamPlayerIds: string[];
  lead: number;
  holesRemaining: number;
};

export const buildMysteryWinnersBySubgame = (params: {
  holeEvents: HoleEventEntry[];
  holeNumber: number;
}): Record<string, { playerId: string; occurredAt: string | null }> => {
  const { holeEvents, holeNumber } = params;
  const map: Record<string, { playerId: string; occurredAt: string | null }> = {};

  holeEvents
    .filter((event) => event.eventType === 'mysteryWinner' && event.holeNumber === holeNumber)
    .forEach((event) => {
      if (!event.eventValue) return;
      map[event.eventValue] = {
        playerId: event.playerId,
        occurredAt: event.occurredAt ?? null,
      };
    });

  return map;
};

/**
 * Games to list in a player's merit breakdown.
 *
 * The round total is the sum of *every* leaderboard game-points row
 * (TourRound.cs recomputes it that way), so listing only the round's selected
 * games makes the breakdown read short against the ROUND column. Two kinds of row
 * fall outside the selection: rows left behind by a game that was deselected
 * after being scored, and rows keyed by the deterministic segment ids that games
 * like Nassau generate, which are never game definitions at all.
 *
 * Selected games are listed first and keep their zero rows, so the breakdown
 * still shows what the round is playing; anything else appears only when it
 * actually carries points.
 */
export const resolveBreakdownGameIds = (params: {
  selectedGameIds: Iterable<string>;
  availableGameIds: Iterable<string>;
  pointsByGameId: Map<string, { points: number }>;
}): string[] => {
  const available = new Set(params.availableGameIds);
  const selected = Array.from(params.selectedGameIds).filter((id) => available.has(id));
  const base = selected.length > 0 ? selected : Array.from(available);

  const seen = new Set(base);
  const scoring = Array.from(params.pointsByGameId.entries())
    .filter(([gameId, value]) => (value?.points ?? 0) !== 0 && !seen.has(gameId))
    .map(([gameId]) => gameId);

  return [...base, ...scoring];
};

export type LeaderboardChangeGameEntry = {
  position: number;
  /** The name printed on the chip; anyone level with them is counted in `sharedWith`. */
  name: string;
  sharedWith: number;
  score: number;
};

export type LeaderboardChangeGameLine = {
  gameId: string;
  gameName: string;
  isTeamGame: boolean;
  entries: LeaderboardChangeGameEntry[];
};

/**
 * Podium for the hole-complete "Leaderboard Changed" modal: who leads each game the round
 * is playing, and by how much.
 *
 * Built from the rows the results table already renders, so the score on a chip is the same
 * score points the table shows — never the leaderboard's merit points, which look like game
 * points but measure the placement ladder instead. Ranking is derived from those same scores
 * so the rank pill and the number beside it cannot disagree.
 *
 * A team game (Best Ball, Scramble) lists only the leader — second place says nothing while a
 * format that pays the best ball is still running — while an individual game lists its first
 * `maxPositions` places. A game nobody has scored yet is left out rather than shown as a
 * field-wide tie on zero.
 */
export const buildLeaderboardChangeGameLines = (params: {
  rows: GameResultRow[];
  gameResultPlayers: Player[];
  gameDefinitionTypes: Record<string, string>;
  orderedTeams: TeamSummary[];
  /** Round-level teams, used for the games in `roundTeamGameIds` instead of `orderedTeams`. */
  roundTeams?: TeamSummary[];
  roundTeamGameIds?: ReadonlySet<string>;
  maxPositions?: number;
}): LeaderboardChangeGameLine[] => {
  const { rows, gameResultPlayers, gameDefinitionTypes, orderedTeams, roundTeams = [], roundTeamGameIds, maxPositions = 2 } = params;

  const indexTeams = (teams: TeamSummary[]) => {
    const byPlayerKey = new Map<string, TeamSummary>();
    teams.forEach((team) => {
      (team.players ?? []).forEach((playerId) => byPlayerKey.set(normalizePlayerKey(playerId), team));
    });
    return byPlayerKey;
  };
  const ballTeamByPlayerKey = indexTeams(orderedTeams);
  const roundTeamByPlayerKey = indexTeams(roundTeams);
  const playerNameById = new Map(gameResultPlayers.map((player) => [player.id, player.name]));
  const teamDisplayName = (team: TeamSummary) => {
    const name = (team.name ?? '').trim();
    if (name.length > 0 && !isUuidLike(name)) return name;
    return (team.players ?? [])
      .map((playerId) => playerNameById.get(playerId))
      .filter((value): value is string => !!value)
      .join(' & ');
  };

  return rows
    .map((row): LeaderboardChangeGameLine | null => {
      const isTeamGame = (gameDefinitionTypes[row.key] ?? '').toLowerCase() === 'team';
      const teamByPlayerKey = roundTeamGameIds?.has(row.key) ? roundTeamByPlayerKey : ballTeamByPlayerKey;

      // Competitors are players, or teams carrying their best member's score — every member of a
      // team already holds the team total, so the max is harmless when one member has no score.
      const scoreByCompetitor = new Map<string, number>();
      row.scores.forEach((score, index) => {
        if (typeof score !== 'number') return;
        const player = gameResultPlayers[index];
        if (!player) return;
        let label = player.name;
        if (isTeamGame) {
          const team = teamByPlayerKey.get(normalizePlayerKey(player.id));
          if (!team) return;
          label = teamDisplayName(team);
        }
        if (!label) return;
        scoreByCompetitor.set(label, Math.max(scoreByCompetitor.get(label) ?? Number.NEGATIVE_INFINITY, score));
      });

      const ranked = [...scoreByCompetitor.entries()]
        .map(([name, score]) => ({ name, score }))
        .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));

      const listedPositions = isTeamGame ? 1 : maxPositions;
      const entries: LeaderboardChangeGameEntry[] = [];
      let position = 1;
      for (let i = 0; i < ranked.length && position <= listedPositions; ) {
        const { score } = ranked[i];
        const tied = ranked.slice(i).filter((entry) => entry.score === score);
        // Nothing scored yet is not a lead: a field level on zero says less than no line at all.
        if (score > 0) {
          entries.push({ position, name: tied[0].name, sharedWith: tied.length - 1, score });
        }
        position += tied.length;
        i += tied.length;
      }

      if (entries.length === 0) return null;
      return { gameId: row.key, gameName: row.name, isTeamGame, entries };
    })
    .filter((line): line is LeaderboardChangeGameLine => line !== null);
};

export const buildDisplayTotals = (params: Parameters<typeof buildDisplayTotalsImpl>[0]) =>
  timeSync('goffy.frontend.hole.selector.duration', { selector: 'buildDisplayTotals' }, () =>
    buildDisplayTotalsImpl(params)
  );

export const buildGameResultRows = (params: Parameters<typeof buildGameResultRowsImpl>[0]) =>
  timeSync('goffy.frontend.hole.selector.duration', { selector: 'buildGameResultRows' }, () =>
    buildGameResultRowsImpl(params)
  );

